import { useCallback, useEffect, useRef, useState } from 'react';
import { GET_USER_PROVISIONING_STATUS, RETRY_USER_PROVISIONING } from 'mxp-graphql-queries';
import { Provisioning } from 'mxp-schemas';
import { ConsumerAuthHelper } from 'utils/auth/consumer';
import request, { resetClient as resetGraphQLClient } from 'utils/GraphQLClient';

const POLL_INTERVAL_MS = 1500;
const NOT_FOUND_MESSAGE_DELAY_MS = 15000;
const NOT_FOUND_TIMEOUT_MS = 25000;
const PROVISIONING_SLOW_MESSAGE_DELAY_MS = 35000;
const PROVISIONING_TIMEOUT_MS = 45000;

export type ProvisioningScreenState = 'provisioning' | 'refreshing' | 'failed' | 'not-found' | 'timeout';

interface ProvisioningHookState {
  message: string;
  retry: () => Promise<void>;
  screenState: ProvisioningScreenState;
  status?: Provisioning.UserProvisioningStatus;
}

interface UseProvisioningStatusParams {
  authHelper: ConsumerAuthHelper | null;
  enabled: boolean;
  onComplete: (associationRedirectUrl?: string) => Promise<void>;
  onSessionExpired: () => void;
}

const getDefaultMessage = (status?: Provisioning.UserProvisioningStatus): string => {
  switch (status) {
    case Provisioning.UserProvisioningStatus.FAILED:
      return 'We encountered an issue while setting up your account. Please try again.';
    case Provisioning.UserProvisioningStatus.NOT_FOUND:
      return 'It looks like your setup is taking longer than expected to start.';
    case Provisioning.UserProvisioningStatus.PROVISIONING:
    case Provisioning.UserProvisioningStatus.COMPLETE:
    default:
      return '';
  }
};

const getProvisioningMessage = (elapsedMs: number): string =>
  elapsedMs >= PROVISIONING_SLOW_MESSAGE_DELAY_MS
    ? 'It looks like your setup is taking longer than expected to complete.'
    : getDefaultMessage(Provisioning.UserProvisioningStatus.PROVISIONING);

export const useProvisioningStatus = ({
  authHelper,
  enabled,
  onComplete,
  onSessionExpired,
}: UseProvisioningStatusParams): ProvisioningHookState => {
  const [screenState, setScreenState] = useState<ProvisioningScreenState>('provisioning');
  const [message, setMessage] = useState<string>(getDefaultMessage(Provisioning.UserProvisioningStatus.PROVISIONING));
  const [status, setStatus] = useState<Provisioning.UserProvisioningStatus>();

  const timeoutRef = useRef<number>();
  const cancelledRef = useRef(false);
  const workflowStartRef = useRef(0);
  const notFoundSinceRef = useRef<number | null>(null);
  const pollProvisioningStatusRef = useRef<() => Promise<void>>(async () => undefined);

  const clearPendingPoll = useCallback(() => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
  }, []);

  const getAccessToken = useCallback(async () => {
    if (!authHelper) return null;
    const accessToken = await authHelper.getValidAccessToken();
    if (!accessToken) {
      onSessionExpired();
      return null;
    }

    return accessToken;
  }, [authHelper, onSessionExpired]);

  const getProvisioningResponse = useCallback(
    async (
      query: string,
      responseKey: 'getUserProvisioningStatus' | 'retryUserProvisioning'
    ): Promise<Provisioning.UserProvisioningResponse | null> => {
      const accessToken = await getAccessToken();
      if (!accessToken) return null;

      try {
        const response = await request(query);
        return response?.[responseKey] || null;
      } catch (error: any) {
        const status = error?.response?.status || error?.primaryApplicationError?.status;
        if (status === 401 || status === 403) {
          onSessionExpired();
          return null;
        }
        throw error;
      }
    },
    [getAccessToken, onSessionExpired]
  );

  const schedulePoll = useCallback(
    (fn: () => Promise<void>) => {
      clearPendingPoll();
      timeoutRef.current = window.setTimeout(() => {
        fn();
      }, POLL_INTERVAL_MS);
    },
    [clearPendingPoll]
  );

  const handleComplete = useCallback(
    async (associationRedirectUrl?: string): Promise<boolean> => {
      if (!authHelper) return false;

      setScreenState('refreshing');
      setMessage('');

      // TEMP: B2B Enterprise writes AICPAUID only after we hand the user back, so gating their
      // redirect on that claim would poll forever. Renewal still runs first, so anyone staying in RAVE
      // leaves with the claim; without a redirect URL it is still required.
      const refreshedClaims = await authHelper.renewProvisioningTokens();
      if (!refreshedClaims?.AICPAUID && !associationRedirectUrl) {
        setScreenState('provisioning');
        setMessage(getDefaultMessage(Provisioning.UserProvisioningStatus.PROVISIONING));
        return false;
      }

      // After successful provisioning, we need to reset the GraphQL client to clear any cached tokens
      // and ensure that subsequent requests use the new tokens.
      resetGraphQLClient();

      await onComplete(associationRedirectUrl);
      return true;
    },
    [authHelper, onComplete]
  );

  const handleProvisioningResponse = useCallback(
    async (response: Provisioning.UserProvisioningResponse | null): Promise<void> => {
      if (!response || cancelledRef.current) return;

      const now = Date.now();
      setStatus(response.status);

      switch (response.status) {
        case Provisioning.UserProvisioningStatus.COMPLETE: {
          const didComplete = await handleComplete(response.associationRedirectUrl);
          if (!didComplete && !cancelledRef.current) {
            schedulePoll(pollProvisioningStatusRef.current);
          }
          return;
        }
        case Provisioning.UserProvisioningStatus.FAILED:
          clearPendingPoll();
          setScreenState('failed');
          setMessage(response.message || getDefaultMessage(response.status));
          return;
        case Provisioning.UserProvisioningStatus.NOT_FOUND:
          if (!notFoundSinceRef.current) {
            notFoundSinceRef.current = now;
          }

          const notFoundElapsedMs = now - notFoundSinceRef.current;

          if (notFoundElapsedMs >= NOT_FOUND_TIMEOUT_MS) {
            clearPendingPoll();
            setScreenState('not-found');
            setMessage(getDefaultMessage(Provisioning.UserProvisioningStatus.FAILED));
            return;
          }

          setScreenState('provisioning');
          setMessage(
            notFoundElapsedMs >= NOT_FOUND_MESSAGE_DELAY_MS
              ? getDefaultMessage(response.status)
              : getDefaultMessage(Provisioning.UserProvisioningStatus.PROVISIONING)
          );
          schedulePoll(pollProvisioningStatusRef.current);
          return;
        case Provisioning.UserProvisioningStatus.PROVISIONING:
        default:
          notFoundSinceRef.current = null;
          const provisioningElapsedMs = now - workflowStartRef.current;

          if (provisioningElapsedMs >= PROVISIONING_TIMEOUT_MS) {
            clearPendingPoll();
            setScreenState('timeout');
            setMessage(getDefaultMessage(Provisioning.UserProvisioningStatus.FAILED));
            return;
          }

          setScreenState('provisioning');
          setMessage(getProvisioningMessage(provisioningElapsedMs));
          schedulePoll(pollProvisioningStatusRef.current);
      }
    },
    [clearPendingPoll, handleComplete, schedulePoll]
  );

  const pollProvisioningStatus = useCallback(async () => {
    if (!authHelper || cancelledRef.current) return;

    try {
      const response = await getProvisioningResponse(GET_USER_PROVISIONING_STATUS, 'getUserProvisioningStatus');
      await handleProvisioningResponse(response);
    } catch {
      if (cancelledRef.current) return;
      clearPendingPoll();
      setStatus(Provisioning.UserProvisioningStatus.FAILED);
      setScreenState('failed');
      setMessage(getDefaultMessage(Provisioning.UserProvisioningStatus.FAILED));
    }
  }, [authHelper, clearPendingPoll, getProvisioningResponse, handleProvisioningResponse]);

  pollProvisioningStatusRef.current = pollProvisioningStatus;

  const retry = useCallback(async () => {
    if (!authHelper || !enabled) return;

    clearPendingPoll();
    workflowStartRef.current = Date.now();
    notFoundSinceRef.current = null;
    setStatus(Provisioning.UserProvisioningStatus.PROVISIONING);
    setScreenState('provisioning');
    setMessage(getDefaultMessage(Provisioning.UserProvisioningStatus.PROVISIONING));

    try {
      const response = await getProvisioningResponse(RETRY_USER_PROVISIONING, 'retryUserProvisioning');
      await handleProvisioningResponse(response);
    } catch {
      if (cancelledRef.current) return;
      setStatus(Provisioning.UserProvisioningStatus.FAILED);
      setScreenState('failed');
      setMessage(getDefaultMessage(Provisioning.UserProvisioningStatus.FAILED));
    }
  }, [authHelper, clearPendingPoll, enabled, getProvisioningResponse, handleProvisioningResponse]);

  useEffect(() => {
    cancelledRef.current = false;

    if (!enabled || !authHelper) {
      clearPendingPoll();
      return () => {
        cancelledRef.current = true;
      };
    }

    workflowStartRef.current = Date.now();
    notFoundSinceRef.current = null;
    setStatus(Provisioning.UserProvisioningStatus.PROVISIONING);
    setScreenState('provisioning');
    setMessage(getDefaultMessage(Provisioning.UserProvisioningStatus.PROVISIONING));
    pollProvisioningStatus();

    return () => {
      cancelledRef.current = true;
      clearPendingPoll();
    };
  }, [authHelper, clearPendingPoll, enabled, pollProvisioningStatus]);

  return {
    screenState,
    status,
    message,
    retry,
  };
};
